export const LIVE_PCM_RATE = 16000;
export const STARTUP_AUDIO_LIMIT_MS = 30000;
const MAX_STARTUP_BYTES = LIVE_PCM_RATE * 2 * STARTUP_AUDIO_LIMIT_MS / 1000;

// Owns audio processing, not the MediaStream tracks (the workspace owns those).
// Keep one ordered queue until the caller authorizes provider input. No token cache.
export async function createBufferedLiveAudio(mediaStream, { onError = () => {}, signal } = {}) {
  let context;
  let source;
  let node;
  let pushStream;
  let closed = false;
  let finishing = false;
  let live = false;
  let bytes = 0;
  let queue = [];
  let startupTimer;
  let finishTimer;
  let resolveFinish;
  let finishPromise;
  let resolveReady;
  let rejectReady;
  let hasAudio = false;
  const ready = new Promise((resolve, reject) => { resolveReady = resolve; rejectReady = reject; });
  // Setup can fail before a caller has a chance to await ready.
  ready.catch(() => {});
  const onAbort = () => { if (!finishing) cleanup(); };
  const cleanup = () => {
    if (closed) return;
    closed = true;
    clearTimeout(startupTimer);
    clearTimeout(finishTimer);
    queue = [];
    if (node) { node.port.onmessage = null; node.onprocessorerror = null; node.disconnect(); }
    source?.disconnect();
    try { pushStream?.close(); } catch (_) {}
    signal?.removeEventListener('abort', onAbort);
    if (context) { context.onstatechange = null; void context.close().catch(() => {}); }
    if (!hasAudio) rejectReady(new Error('Audio capture was cancelled before recording started.'));
    resolveFinish?.();
  };
  const fail = message => {
    if (closed) return;
    const error = new Error(message);
    rejectReady(error);
    cleanup();
    onError(error);
  };
  try {
    if (!window.AudioContext || !window.AudioWorkletNode) {
      throw new Error('Startup audio capture requires a supported browser. Please use current Chrome or Safari.');
    }
    if (signal?.aborted) throw new Error('Audio capture was cancelled.');
    context = new AudioContext();
    signal?.addEventListener('abort', onAbort, { once: true });
    startupTimer = setTimeout(() => fail('Transcription took too long to connect. Please start again.'), STARTUP_AUDIO_LIMIT_MS);
    await context.resume();
    if (closed) throw new Error('Audio capture was cancelled.');
    if (context.state !== 'running') throw new Error('Could not start audio capture. Check your microphone and try again.');
    await context.audioWorklet.addModule(`${process.env.PUBLIC_URL || ''}/audio/live-pcm-processor.js`);
    if (closed) throw new Error('Audio capture was cancelled.');
    context.onstatechange = () => {
      if (!closed && !finishing && context.state !== 'running') fail('Audio capture was interrupted. Please start transcription again.');
    };
    source = context.createMediaStreamSource(mediaStream);
    node = new AudioWorkletNode(context, 'live-pcm-processor', {
      channelCount: 1, channelCountMode: 'explicit', outputChannelCount: [1],
    });
    node.onprocessorerror = () => fail('Audio processing stopped. Please start transcription again.');
    node.port.onmessage = ({ data }) => {
      if (closed) return;
      if (data.finished) { cleanup(); return; }
      if (!(data.pcm instanceof ArrayBuffer) || !data.pcm.byteLength) return;
      if (!live) {
        bytes += data.pcm.byteLength;
        if (bytes > MAX_STARTUP_BYTES) { fail('Transcription took too long to connect. Please start again.'); return; }
      }
      if (!hasAudio) { hasAudio = true; resolveReady(); }
      try {
        if (pushStream) pushStream.write(data.pcm);
        else queue.push(data.pcm);
      } catch (_) { fail('Could not send audio for transcription. Please start again.'); }
    };
    source.connect(node);
    node.connect(context.destination);
    return {
      ready,
      // Called only after the wallet-backed session is authorized. The SDK queues
      // these bytes until Azure is connected; new samples follow in the same stream.
      attach(stream) {
        if (closed || pushStream) throw new Error('Audio capture is no longer available.');
        pushStream = stream;
        try { for (const chunk of queue) pushStream.write(chunk); }
        catch (error) { cleanup(); throw error; }
        queue = [];
      },
      markLive() {
        if (closed || !pushStream) throw new Error('Audio capture stopped before transcription connected.');
        live = true;
        clearTimeout(startupTimer);
      },
      finish() {
        if (finishPromise) return finishPromise;
        if (closed) return Promise.resolve();
        finishing = true;
        // Stop new microphone input now, but flush the worklet's final partial chunk.
        source.disconnect();
        finishPromise = new Promise(resolve => { resolveFinish = resolve; });
        finishTimer = setTimeout(() => fail('Could not finish sending the last audio. Your transcript so far is kept.'), 500);
        try { node.port.postMessage('finish'); } catch (_) { fail('Could not finish sending the last audio. Your transcript so far is kept.'); }
        return finishPromise;
      },
      dispose() { if (!finishing) cleanup(); },
    };
  } catch (error) { cleanup(); throw error; }
}
